import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDarkMode } from '../context/DarkModeContext';
import Footer from '../components/Footer';
import '../styles/TextCheckerPage.css';
import { Button, Box } from '@mui/material';
import { Home } from '@mui/icons-material';

export default function TextCheckerPage() {
  const [text, setText] = useState('');
  const [textStats, setTextStats] = useState(null);
  const [specialChars, setSpecialChars] = useState(null);
  const [grammarResults, setGrammarResults] = useState(null);
  const [rephraseResult, setRephraseResult] = useState(null);
  const [showStats, setShowStats] = useState(false);
  const [showSpecialChars, setShowSpecialChars] = useState(false);
  const { darkMode, toggleDarkMode } = useDarkMode();
  const navigate = useNavigate();

  const calculateStats = () => {
    if (!text.trim()) {
      setTextStats(null);
      return;
    }

    // Clear previous results
    setSpecialChars(null);
    setGrammarResults(null);
    setRephraseResult(null);
    setShowSpecialChars(false);

    const words = text.trim().split(/\s+/).filter(word => word.length > 0);
    const sentences = text.split(/[.!?]+/).filter(sentence => sentence.trim().length > 0);
    const chars = text.length;
    const charsNoSpaces = text.replace(/\s/g, '').length;
    const paragraphs = text.split('\n\n').filter(para => para.trim().length > 0);
    const lines = text.split('\n').length;

    setTextStats({
      wordCount: words.length,
      charCount: chars,
      charNoSpacesCount: charsNoSpaces,
      sentenceCount: sentences.length,
      paragraphCount: paragraphs.length,
      lineCount: lines,
      averageWordLength: (charsNoSpaces / words.length).toFixed(1)
    });
    setShowStats(true);
  };

  const findSpecialChars = () => {
    if (!text.trim()) {
      setSpecialChars(null);
      return;
    }

    // Clear previous results
    setTextStats(null);
    setGrammarResults(null);
    setRephraseResult(null);
    setShowStats(false);

    // Create visualized text with special characters
    let visualizedText = '';
    let currentLine = '';
    let charIndex = 0;
    let charCount = 0;
    let byteCount = 0;

    // Convert text to array of characters to preserve all control characters
    const chars = Array.from(text);
    
    // Process text character by character
    while (charIndex < chars.length) {
      const char = chars[charIndex];
      const nextChar = charIndex + 1 < chars.length ? chars[charIndex + 1] : null;
      
      charCount++;
      byteCount += new Blob([char]).size;
      
      if (char === '\r' && nextChar === '\n') {
        // Handle CRLF sequence
        currentLine += '↵';
        charIndex += 2;
        visualizedText += currentLine + '\n';
        currentLine = '';
      } else if (char === '\r' || char === '\n') {
        // Handle any line ending
        currentLine += '↵';
        charIndex++;
        visualizedText += currentLine + '\n';
        currentLine = '';
      } else {
        // Handle other characters
        if (char === '\t') {
          currentLine += '→';
        } else if (char === ' ') {
          currentLine += '·';
        } else {
          currentLine += char;
        }
        charIndex++;
      }
    }

    // Add the last line if it exists
    if (currentLine) {
      visualizedText += currentLine + '\n';
    }

    setSpecialChars({
      visualizedText,
      charCount,
      byteCount
    });
    setShowSpecialChars(true);
  };

  const sentenceCase = () => {
    if (!text.trim()) {
      alert('Please enter some text first!');
      return;
    }
    
    // Clear previous results
    setTextStats(null);
    setSpecialChars(null);
    setGrammarResults(null);
    setRephraseResult(null);
    setShowStats(false);
    setShowSpecialChars(false);
    
    const lines = text.split('\n');
    const transformedLines = lines.map(line => {
      if (line.trim() === '') return line;
      return line.charAt(0).toUpperCase() + line.slice(1).toLowerCase();
    });
    
    setText(transformedLines.join('\n'));
  };

  const upperCase = () => {
    if (!text.trim()) {
      alert('Please enter some text first!');
      return;
    }
    
    // Clear previous results
    setTextStats(null);
    setSpecialChars(null);
    setGrammarResults(null);
    setRephraseResult(null);
    setShowStats(false);
    setShowSpecialChars(false);
    
    setText(text.toUpperCase());
  };

  const lowerCase = () => {
    if (!text.trim()) {
      alert('Please enter some text first!');
      return;
    }
    
    // Clear previous results
    setTextStats(null);
    setSpecialChars(null);
    setGrammarResults(null);
    setRephraseResult(null);
    setShowStats(false);
    setShowSpecialChars(false);
    
    setText(text.toLowerCase());
  };

  const copyToClipboard = async () => {
    if (!text.trim()) {
      alert('No text to copy!');
      return;
    }
    
    try {
      await navigator.clipboard.writeText(text);
      alert('Text copied to clipboard!');
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      alert('Text copied to clipboard!');
    }
  };

  const handleClear = () => {
    setText('');
    setTextStats(null);
    setSpecialChars(null);
    setGrammarResults(null);
    setRephraseResult(null);
    setShowStats(false);
    setShowSpecialChars(false);
  };

  return (
    <div className={`container ${darkMode ? 'dark-theme' : ''}`} style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Dark mode toggle in top right corner */}
      <button
        onClick={toggleDarkMode}
        className="dark-mode-toggle"
        style={{ 
          position: 'fixed',
          top: '20px',
          right: '20px',
          zIndex: 1000,
          color: '#fff',
          fontSize: '1rem',
          padding: '8px 16px',
          borderRadius: '4px',
          backgroundColor: '#388e3c',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: '40px'
        }}
        onMouseEnter={(e) => e.target.style.backgroundColor = '#2e7d32'}
        onMouseLeave={(e) => e.target.style.backgroundColor = '#388e3c'}
      >
        <span className="dark-mode-text">
          {darkMode ? '☀️ Light Mode' : '🌙 Dark Mode'}
        </span>
        <span className="dark-mode-icon">
          {darkMode ? '☀️' : '🌙'}
        </span>
      </button>

      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', sm: 'center' },
        mb: 3,
        gap: { xs: 2, sm: 0 }
      }}>
        <Button
          startIcon={<Home />}
          onClick={() => navigate('/')}
          sx={{ 
            color: '#fff',
            textDecoration: 'none',
            fontSize: '1rem',
            padding: '8px 16px',
            borderRadius: '4px',
            bgcolor: '#388e3c',
            textTransform: 'none',
            width: { xs: '100%', sm: 'auto' },
            '&:hover': {
              bgcolor: '#2e7d32'
            }
          }}
        >
          Home
        </Button>
        <h1 className="title" style={{ color: darkMode ? '#4CAF50' : '#006400', fontWeight: 'bold', textAlign: 'center', flexGrow: 1, fontSize: '2.5rem', margin: 0 }}>
          Text Checker
        </h1>
      </Box>

      <div className="text-checker-container">
        <div className="input-section">
          <div className="text-input-container">
            <textarea
              className="text-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Enter your text here..."
              rows={10}
            />
            <button 
              className="copy-button"
              onClick={copyToClipboard}
              title="Copy text to clipboard"
              style={{ position: 'absolute', top: 10, right: 10, background: 'transparent', border: 'none', padding: 0, margin: 0, zIndex: 10, cursor: 'pointer' }}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={darkMode ? '#fff' : '#222'} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="8" y="8" width="12" height="12" rx="3"/>
                <rect x="4" y="4" width="12" height="12" rx="3"/>
              </svg>
            </button>
          </div>
          <div className="button-group">
            <button className="action-button" onClick={calculateStats}>
              Word & Letter Count
            </button>
            <button className="action-button" onClick={findSpecialChars}>
            Find Special Characters
            </button>
            <button className="action-button" onClick={sentenceCase}>
              Sentence case
            </button>
            <button className="action-button" onClick={() => {
              // Capitalized Case (Title Case)
              if (!text.trim()) {
                alert('Please enter some text first!');
                return;
              }
              setTextStats(null);
              setSpecialChars(null);
              setGrammarResults(null);
              setRephraseResult(null);
              setShowStats(false);
              setShowSpecialChars(false);
              const newText = text.replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
              setText(newText);
            }}>
              Capitalized Case
            </button>
            <button className="action-button" onClick={upperCase}>
              UPPER CASE
            </button>
            <button className="action-button" onClick={lowerCase}>
              lower case
            </button>
            <button className="action-button" onClick={() => {
              // Remove blank lines
              if (!text.trim()) {
                alert('No text to process!');
                return;
              }
              setTextStats(null);
              setSpecialChars(null);
              setGrammarResults(null);
              setRephraseResult(null);
              setShowStats(false);
              setShowSpecialChars(false);
              const newText = text.split('\n').filter(line => line.trim() !== '').join('\n');
              setText(newText);
            }}>
              Remove Blank Lines
            </button>

            <button className="clear-button" onClick={handleClear}>
              🗑️ Clear
            </button>
          </div>
        </div>

        <div className="results-section">
          {showStats && textStats && (
            <div className="result-card">
              <h3>Text Statistics</h3>
              <ul className="stats-list">
                <li>Words: {textStats.wordCount}</li>
                <li>Characters (with spaces): {textStats.charCount}</li>
                <li>Characters (no spaces): {textStats.charNoSpacesCount}</li>
                <li>Sentences: {textStats.sentenceCount}</li>
                <li>Paragraphs: {textStats.paragraphCount}</li>
                <li>Lines: {textStats.lineCount}</li>
                <li>Average Word Length: {textStats.averageWordLength}</li>
              </ul>
            </div>
          )}

          {showSpecialChars && specialChars && (
            <div className="result-card special-chars-result">
              <h3>Special Characters</h3>
              <div className="visualized-text">
                <pre className="text-display">{specialChars.visualizedText}</pre>
              </div>
              <div className="stats">
                {specialChars.charCount} characters, {specialChars.byteCount} bytes
              </div>
            </div>
          )}

          {grammarResults && (
            <div className="result-card">
              <h3>Grammar Check Results</h3>
              {/* Grammar check results will be displayed here */}
            </div>
          )}

          {rephraseResult && (
            <div className="result-card">
              <h3>Rephrased Text</h3>
              {/* Rephrased text will be displayed here */}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
} 